import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useFiltersStore, type Filter, type FilterAction, type FilterCondition, type FilterActionType, type FilterConditionField, type FilterConditionOperator } from '../../stores/filtersStore';

// Map frontend action types to backend enum values
const actionTypeMap: Record<string, FilterActionType> = {
  move: 'move_to_folder',
  copy: 'move_to_folder', // Backend doesn't have copy, use move
  markRead: 'mark_as_read',
  markImportant: 'mark_as_important',
  star: 'mark_as_starred',
  delete: 'delete',
  forward: 'forward',
  label: 'add_label',
  spam: 'mark_as_spam',
};

// Reverse map for display
const reverseActionTypeMap: Record<FilterActionType, string> = {
  move_to_folder: 'move',
  add_label: 'label',
  mark_as_read: 'markRead',
  mark_as_starred: 'star',
  mark_as_spam: 'spam',
  delete: 'delete',
  forward: 'forward',
  mark_as_important: 'markImportant',
};

// Map frontend condition operators to backend enum values
const operatorMap: Record<string, FilterConditionOperator> = {
  contains: 'contains',
  notContains: 'not_contains',
  is: 'equals',
  isNot: 'not_equals',
  startsWith: 'starts_with',
  endsWith: 'ends_with',
  greaterThan: 'greater_than',
  lessThan: 'less_than',
};

const reverseOperatorMap: Record<FilterConditionOperator, string> = {
  contains: 'contains',
  not_contains: 'notContains',
  equals: 'is',
  not_equals: 'isNot',
  starts_with: 'startsWith',
  ends_with: 'endsWith',
  greater_than: 'greaterThan',
  less_than: 'lessThan',
  matches_regex: 'matchesRegex',
};

const FiltersSettings: React.FC = () => {
  const { 
    filters, 
    isLoading,
    isSaving,
    error,
    fetchFilters,
    createFilter,
    updateFilter,
    deleteFilter,
    toggleFilter,
    clearError,
  } = useFiltersStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<Filter | null>(null);
  const [filterName, setFilterName] = useState('');
  const [conditionField, setConditionField] = useState<FilterConditionField>('from');
  const [conditionOperator, setConditionOperator] = useState('contains');
  const [conditionValue, setConditionValue] = useState('');
  const [actionType, setActionType] = useState('move');
  const [actionValue, setActionValue] = useState('');
  const [matchType, setMatchType] = useState<'all' | 'any'>('all');
  const [stopProcessing, setStopProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch filters on component mount
  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // Show error snackbar when error occurs
  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      clearError();
    }
  }, [error, clearError]);

  const handleOpenDialog = (filter?: Filter) => {
    if (filter) {
      setEditingFilter(filter);
      setFilterName(filter.name);
      setMatchType(filter.matchType || 'all');
      setStopProcessing(filter.stopProcessing || false);
      
      if (filter.conditions.length > 0) {
        const cond = filter.conditions[0];
        setConditionField(cond.field);
        setConditionOperator(reverseOperatorMap[cond.operator] || 'contains');
        setConditionValue(String(cond.value));
      }
      if (filter.actions.length > 0) {
        const action = filter.actions[0];
        setActionType(reverseActionTypeMap[action.type] || 'move');
        setActionValue(action.value || '');
      }
    } else {
      setEditingFilter(null);
      setFilterName('');
      setConditionField('from');
      setConditionOperator('contains');
      setConditionValue('');
      setActionType('move');
      setActionValue('');
      setMatchType('all');
      setStopProcessing(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFilter(null);
  };

  const handleSaveFilter = async () => {
    const backendOperator = operatorMap[conditionOperator] || 'contains';
    const backendActionType = actionTypeMap[actionType] || 'move_to_folder';

    const filterData = {
      name: filterName,
      conditions: [{
        field: conditionField,
        operator: backendOperator,
        value: conditionValue,
      }] as FilterCondition[],
      actions: [{
        type: backendActionType,
        value: actionValue || undefined,
      }] as FilterAction[],
      matchType,
      isActive: editingFilter?.isActive ?? true,
      stopProcessing,
    };

    try {
      if (editingFilter) {
        const filterId = editingFilter._id || editingFilter.id;
        if (filterId) {
          await updateFilter(filterId, filterData);
          setSnackbar({ open: true, message: 'Filter updated successfully', severity: 'success' });
        }
      } else {
        await createFilter(filterData);
        setSnackbar({ open: true, message: 'Filter created successfully', severity: 'success' });
      }
      handleCloseDialog();
    } catch (err) {
      // Error is handled by the store
    }
  };

  const handleDeleteFilter = async (id: string) => {
    try {
      await deleteFilter(id);
      setSnackbar({ open: true, message: 'Filter deleted', severity: 'success' });
    } catch (err) {
      // Error is handled by the store
    }
  };

  const handleToggleFilter = async (id: string) => {
    try {
      await toggleFilter(id);
      setSnackbar({ open: true, message: 'Filter toggled', severity: 'success' });
    } catch (err) {
      // Error is handled by the store
    }
  };

  const getActionLabel = (action: FilterAction) => {
    const labels: Record<FilterActionType, string> = {
      move_to_folder: `Move to ${action.value || 'folder'}`,
      add_label: `Add label: ${action.value}`,
      mark_as_read: 'Mark as read',
      mark_as_starred: 'Add star',
      mark_as_spam: 'Mark as spam',
      delete: 'Delete',
      forward: `Forward to ${action.value}`,
      mark_as_important: 'Mark as important',
    };
    return labels[action.type] || action.type;
  };

  const getConditionLabel = (condition: FilterCondition) => {
    const operatorLabels: Record<string, string> = {
      contains: 'contains',
      not_contains: "doesn't contain",
      equals: 'is',
      not_equals: 'is not',
      starts_with: 'starts with',
      ends_with: 'ends with',
      greater_than: '>',
      less_than: '<',
      matches_regex: 'matches',
    };
    const opLabel = operatorLabels[condition.operator] || condition.operator;
    return `${condition.field} ${opLabel} "${condition.value}"`;
  };

  if (isLoading && filters.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 1 }}>
        Filters
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create rules to automatically organize incoming emails
      </Typography>

      {/* Add filter button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Filter
        </Button>
      </Box>

      {/* Filters list */}
      {filters.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ p: 4, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}
        >
          <Typography variant="body1" color="text.secondary">
            No filters created yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create a filter to automatically sort, label, or take action on incoming emails.
          </Typography>
        </Paper>
      ) : (
        <List>
          {filters.map((filter) => {
            const filterId = filter._id || filter.id || '';
            return (
              <Paper
                key={filterId}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  opacity: filter.isActive ? 1 : 0.6,
                }}
              >
                <ListItem
                  secondaryAction={
                    <>
                      <IconButton onClick={() => handleOpenDialog(filter)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteFilter(filterId)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  }
                >
                  <Switch
                    checked={filter.isActive}
                    onChange={() => handleToggleFilter(filterId)}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {filter.name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" component="div">
                          If: {filter.conditions.map(getConditionLabel).join(filter.matchType === 'all' ? ' AND ' : ' OR ')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {filter.actions.map((action, idx) => (
                            <Chip
                              key={idx}
                              label={getActionLabel(action)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              </Paper>
            );
          })}
        </List>
      )}

      {/* Filter dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFilter ? 'Edit Filter' : 'Create Filter'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Filter name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              CONDITIONS
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Select
                value={conditionField}
                onChange={(e) => setConditionField(e.target.value as FilterConditionField)}
                size="small"
                sx={{ minWidth: 100 }}
              >
                <MenuItem value="from">From</MenuItem>
                <MenuItem value="to">To</MenuItem>
                <MenuItem value="subject">Subject</MenuItem>
                <MenuItem value="body">Body</MenuItem>
                <MenuItem value="hasAttachment">Has attachment</MenuItem>
              </Select>
              <Select
                value={conditionOperator}
                onChange={(e) => setConditionOperator(e.target.value)}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="contains">contains</MenuItem>
                <MenuItem value="notContains">doesn't contain</MenuItem>
                <MenuItem value="is">is</MenuItem>
                <MenuItem value="isNot">is not</MenuItem>
                <MenuItem value="startsWith">starts with</MenuItem>
                <MenuItem value="endsWith">ends with</MenuItem>
              </Select>
              <TextField
                value={conditionValue}
                onChange={(e) => setConditionValue(e.target.value)}
                size="small"
                placeholder="Value"
                sx={{ flexGrow: 1, minWidth: 150 }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Select
                    value={matchType}
                    onChange={(e) => setMatchType(e.target.value as 'all' | 'any')}
                    size="small"
                    sx={{ ml: 1, minWidth: 80 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="any">Any</MenuItem>
                  </Select>
                }
                label="Match"
                labelPlacement="start"
                sx={{ ml: 0 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Match all conditions or any condition
              </Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              ACTIONS
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Select
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                size="small"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="move">Move to folder</MenuItem>
                <MenuItem value="markRead">Mark as read</MenuItem>
                <MenuItem value="markImportant">Mark as important</MenuItem>
                <MenuItem value="star">Add star</MenuItem>
                <MenuItem value="spam">Mark as spam</MenuItem>
                <MenuItem value="delete">Delete</MenuItem>
                <MenuItem value="forward">Forward to</MenuItem>
                <MenuItem value="label">Add label</MenuItem>
              </Select>
              {['move', 'forward', 'label'].includes(actionType) && (
                <TextField
                  value={actionValue}
                  onChange={(e) => setActionValue(e.target.value)}
                  size="small"
                  placeholder={
                    actionType === 'forward' ? 'Email address' :
                    actionType === 'label' ? 'Label name' : 'Folder name (e.g., Spam, Archive)'
                  }
                  sx={{ flexGrow: 1, minWidth: 150 }}
                />
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={stopProcessing}
                  onChange={(e) => setStopProcessing(e.target.checked)}
                  size="small"
                />
              }
              label="Stop processing other filters if this matches"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveFilter}
            disabled={!filterName || !conditionValue || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {editingFilter ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FiltersSettings;
